import React, { useState, useRef, useEffect } from 'react';
import { Header, Footer, FAQ, CourseCategories } from '../../components/common';
import { useContactModal } from '../../components/common/ContactModalContext';
import '../../components/common/PageLayout.css';
import './B2B.css';
import b2bBannerImg from '../../images/b2b-banner.png';
import b2bBanner1Img from '../../images/b2b-img-1.jpeg';
import b2bBanner2Img from '../../images/b2b-img-2.jpeg';

const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const IconPlanning = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    <path d="M12 12v4M10 16h4" />
  </svg>
);

const IconAnalytics = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const IconCertification = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    <circle cx="12" cy="12" r="4" />
    <path d="M12 8v4l2 2" />
  </svg>
);

const IconWealth = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <path d="M12 12v.01M17 10h-2a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2" />
    <path d="M7 10h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H7" />
  </svg>
);

const services = [
  {
    id: 1,
    icon: IconPlanning,
    title: 'Tailored Training Curriculum',
    description: 'Developed for your unique needs',
  },
  {
    id: 2,
    icon: IconAnalytics,
    title: 'Expert Facilitators',
    description: 'Industry professionals with proven success',
    path: '/contact',
  },
  {
    id: 3,
    icon: IconCertification,
    title: 'Blended Delivery Models',
    description: 'Live virtual, onsite, or hybrid',
    path: '/courses',
  },
  {
    id: 4,
    icon: IconWealth,
    title: 'Certifications & Assessments',
    description: 'Trackable progress with ROI insights',
    path: '/contact',
  },
];

const processSteps = [
  {
    id: 1,
    title: 'Discovery & Needs Assessment',
    description: 'Understand goals, skill gaps, and business priorities.',
  },
  {
    id: 2,
    title: 'Custom Program Design',
    description: 'Create a curriculum aligned to your strategic roadmap.',
  },
  {
    id: 3,
    title: 'Delivery & Engagement',
    description: 'Learning via expert trainers, labs, workshops, and projects.',
  },
  {
    id: 4,
    title: 'Measurement & Follow-Up',
    description: 'Feedback loops and performance insights to evaluate impact.',
  },
];

const b2bFaqItems = [
  {
    question: 'Do you offer customized corporate training programs?',
    answer: 'Yes. Every organization has unique goals, so we design fully customized training programs based on your team\'s skill gaps, business objectives, and industry requirements. We begin with a needs assessment to ensure the curriculum aligns with your outcomes.',
  },
  {
    question: 'What delivery modes do you offer?',
    answer: 'We offer flexible delivery formats including onsite training, live virtual sessions, and hybrid models. Programs can be structured as workshops, bootcamps, multi-week learning journeys, or intensive short-term training based on your preference.',
  },
  {
    question: 'How long does a corporate training program typically last?',
    answer: 'Program duration depends on scope and complexity. Trainings can range from 1–2 day workshops to 4–8 week structured programs. We help define a timeline that balances learning depth with minimal disruption to your operations.',
  },
  {
    question: 'How do you measure training effectiveness?',
    answer: 'We track progress through assessments, practical projects, feedback surveys, and post-training evaluations. Upon request, we also provide performance reports and skill competency insights to help you measure ROI.',
  },
  {
    question: 'Do participants receive certifications?',
    answer: 'Yes. Participants receive completion certificates from InfinySkills. For specialized programs, we can also prepare learners for industry-recognized certifications where applicable.',
  },
];

const b2bCourseCategories = [
  { label: 'Salesforce Platform', icon: '☁️', path: '/offerings/salesforce' },
  { label: 'Full Stack Development', icon: '🧩', path: '/course/full-stack-web-development' },
  { label: 'Website Development', icon: '</>', path: '/course/web-development' },
  { label: 'Marketing Cloud', icon: '📢', path: '/offerings/salesforce' },
  { label: 'Service Cloud', icon: '🛟', path: '/offerings/salesforce' },
  { label: 'Sales Cloud', icon: '📈', path: '/offerings/salesforce' },
  { label: 'Revenue Cloud', icon: '💰', path: '/offerings/salesforce' },
  { label: 'Agentforce', icon: '🤖', path: '/offerings/salesforce' },
  { label: 'Experience Cloud', icon: '🌐', path: '/offerings/salesforce' },
];

const B2B = () => {
  const [processAnimated, setProcessAnimated] = useState(false);
  const processRef = useRef(null);
  const { openContactModal } = useContactModal();

  useEffect(() => {
    const el = processRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setProcessAnimated(true);
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
  <>
    <div className="hero-gradient-wrap">
      <Header ctaText="Join Community" ctaPath="#courses" />
      <section className="b2b-hero-wrap" aria-label="Hero">
        <div className="b2b-hero-inner">
        <div className="b2b-hero-left">
          <p className="b2b-hero-welcome">For Corporates</p>
          <h1 className="b2b-hero-title">Corporate Upskilling & Enterprise Training</h1>
          <p className="b2b-hero-desc">
          Empower your workforce with tailored training programs designed for real business impact. InfinySkills partners with enterprises to unlock learning outcomes that drive performance, innovation, and growth.
          </p>
          <button type="button" className="b2b-hero-cta" onClick={openContactModal}>
            Ready to Empower?
            <span className="b2b-hero-cta-arrow"><ArrowRight /></span>
          </button>
        </div>
        <div className="b2b-hero-right">
          <div className="b2b-hero-image-wrap">
            <img
              src={b2bBannerImg}
              alt="Team collaboration"
            />
            <div className="b2b-hero-badge b2b-hero-badge--clients">
              <div className="b2b-hero-badge-avatars">
                <span aria-hidden="true" />
                <span aria-hidden="true" />
                <span aria-hidden="true" />
              </div>
              <span>18k+ Happy Learners</span>
            </div>
            {/* <div className="b2b-hero-badge b2b-hero-badge--chart">
              <span>Completions</span>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Last quarter</span>
              <div className="b2b-hero-chart-bars">
                <span className="b2b-hero-chart-bar" aria-hidden="true" />
                <span className="b2b-hero-chart-bar" aria-hidden="true" />
                <span className="b2b-hero-chart-bar" aria-hidden="true" />
              </div>
            </div> */}
          </div>
        </div>
        </div>
      </section>
    </div>

    <main className="page-layout-main">
    {/* About Us */}
    <section className="b2b-about" id="about" aria-labelledby="b2b-about-title">
      <div className="b2b-about-inner">
        <div className="b2b-about-image-main">
          <img
            src={b2bBanner1Img}
            alt="Team reviewing materials"
          />
        </div>
        <div className="b2b-about-content">
         
          <h2 id="b2b-about-title" className="b2b-about-title">Our Path to Trust and Learning Success</h2>
          <p className="b2b-about-text">
          Our corporate training blends industry expertise with practical, hands-on learning — ensuring your teams acquire skills that matter. From leadership development to advanced tech specialization, we deliver measurable outcomes with scalable learning solutions.
          </p>
         
          <div className="b2b-about-image-secondary">
            <img
              src={b2bBanner2Img}
              alt="Collaborative meeting"
            />
          </div>
        </div>
      </div>
    </section>

    {/* What We Do */}
    <section className="b2b-services" id="what-we-do" aria-labelledby="b2b-services-title">
      <div className="b2b-services-inner">
        <header className="b2b-services-header">
          <p className="b2b-services-label">What We Offer</p>
          <h2 id="b2b-services-title" className="b2b-services-title">Specialized Learning & Advisory Services</h2>
        </header>
        <div className="b2b-services-grid">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <article key={s.id} className="b2b-service-card">
                <div className="b2b-service-icon-wrap">
                  <Icon />
                </div>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
                {/* <Link to={s.path} className="b2b-service-link" aria-label={`Learn more about ${s.title}`}>
                  <ArrowRight />
                </Link> */}
              </article>
            );
          })}
        </div>
      </div>
    </section>

    {/* How It Works - Our Training Process: number → title → description, wave behind, steps animate one by one */}
    <section
      ref={processRef}
      className={`b2b-process ${processAnimated ? 'b2b-process--animated' : ''}`}
      id="how-it-works"
      aria-labelledby="b2b-process-title"
    >
      <div className="b2b-process-inner">
        <header className="b2b-process-header">
          <p className="b2b-process-label">How It Works</p>
          <h2 id="b2b-process-title" className="b2b-process-title">Our Training Process</h2>
          <p className="b2b-process-desc">
            Clear, consultative engagement from kickoff to delivery:
          </p>
        </header>
        <div className="b2b-process-wave-wrap">
          <svg className="b2b-process-wave-svg" viewBox="0 0 1000 120" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="b2b-wave-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#35225a" />
                <stop offset="100%" stopColor="#2d1b4e" />
              </linearGradient>
            </defs>
            <path
              className="b2b-process-wave-path"
              d="M 0 60 Q 125 20 250 60 T 500 60 T 750 60 T 1000 60"
              fill="none"
              stroke="url(#b2b-wave-gradient)"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
          <div className="b2b-process-steps">
            {processSteps.map((step, index) => (
              <article
                key={step.id}
                className="b2b-process-step"
                style={{ '--step-delay': `${0.5 + index * 0.5}s` }}
              >
                <span className="b2b-process-step-num">{step.id}</span>
                <h3 className="b2b-process-step-title">{step.title}</h3>
                <p className="b2b-process-step-desc">{step.description}</p>
              </article>
            ))}
          </div>
        </div>
        <div className="b2b-process-cta-wrap">
          <button type="button" className="b2b-process-cta" onClick={openContactModal}>
            Ready to Empower?
            <span className="b2b-process-cta-arrow"><ArrowRight /></span>
          </button>
        </div>
      </div>
    </section>

    <CourseCategories title="Courses We Provide" items={b2bCourseCategories} />

    <FAQ items={b2bFaqItems} title="FAQ" id="faq" />
    </main>
    <Footer />
  </>
  );
};

export default B2B;
